import type {
  CartRecommendationCartProduct,
  CartRecommendationDisplayContext,
  CartRecommendationItem,
} from '@diamond/shared/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { postCartRecommendations } from '../queries/cart';
import { useAuthStore, useShipToStore } from '../stores';
// Dev-only mock data, colocated with this hook (manual toggle below).
import { MOCK_RECOMMENDATIONS } from './__mocks__/mockRecommendations';

export const CART_RECOMMENDATIONS_QUERY_KEY = 'cart-recommendations';
const USE_DUMMY_CART_RECOMMENDATIONS = false; // Set to true to use mock data instead of API

const DUMMY_CART_RECOMMENDATION_ITEMS = MOCK_RECOMMENDATIONS;

const getDummyCartRecommendations = (
  displayContext: CartRecommendationDisplayContext,
  selectedProductItemCodes: number[],
  cartItems: CartRecommendationCartProduct[]
): CartRecommendationItem[] => {
  const selectedCodes = new Set(selectedProductItemCodes);
  const cartItemCodes = cartItems.map((item) => item.product_item_code);

  return (DUMMY_CART_RECOMMENDATION_ITEMS as CartRecommendationItem[])
    .filter(
      (item: CartRecommendationItem) =>
        !selectedCodes.has(Number(item.product.item_code))
    )
    .map((item: CartRecommendationItem, index: number) => ({
      ...item,
      recommendation: {
        ...item.recommendation,
        display_context: displayContext,
        product_cart_ids: cartItems.map((cartItem) => cartItem.product_id),
        product_cart_item_codes: cartItemCodes,
        rank: index + 1,
      },
    }));
};

export interface UseCartRecommendationsReturn {
  recommendations: CartRecommendationItem[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches cart recommendations for a given placement.
 *
 * `cartItems` is provided by the caller (the full cart snapshot from the Cart
 * API) and forwarded as `cart_products`. `warehouseId` is read from the
 * ship-to store. The query is disabled until there is at least one trigger
 * item code and a warehouse, and never retries so a failure simply hides the
 * module without delaying the UX.
 */
export function useCartRecommendations(
  displayContext: CartRecommendationDisplayContext,
  selectedProductItemCodes: number[],
  cartItems: CartRecommendationCartProduct[]
): UseCartRecommendationsReturn {
  const token = useAuthStore((state) => state.access_token);
  const warehouseId = useShipToStore((state) => state.selectedShipToId);

  const hasRecommendationPayload =
    selectedProductItemCodes.length > 0 && cartItems.length > 0;
  const enabled =
    !USE_DUMMY_CART_RECOMMENDATIONS &&
    Boolean(token) &&
    Boolean(warehouseId) &&
    hasRecommendationPayload;

  const dummyRecommendations = useMemo(
    () =>
      USE_DUMMY_CART_RECOMMENDATIONS && hasRecommendationPayload
        ? getDummyCartRecommendations(
            displayContext,
            selectedProductItemCodes,
            cartItems
          )
        : [],
    [
      cartItems,
      displayContext,
      hasRecommendationPayload,
      selectedProductItemCodes,
    ]
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      CART_RECOMMENDATIONS_QUERY_KEY,
      displayContext,
      selectedProductItemCodes,
      cartItems,
      warehouseId,
    ],
    queryFn: () =>
      postCartRecommendations(token, {
        warehouse_id: warehouseId,
        display_context: displayContext,
        selected_product_item_codes: selectedProductItemCodes,
        cart_products: cartItems,
      }),
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: 0,
  });

  if (USE_DUMMY_CART_RECOMMENDATIONS) {
    return {
      recommendations: dummyRecommendations,
      isLoading: false,
      isError: false,
    };
  }

  return {
    recommendations: data?.data ?? [],
    isLoading,
    isError,
  };
}
